import React, { useEffect, useState } from "react";
import { Card } from "../components/ui/card";
import { Heart, Users, ShieldCheck, PiggyBank, ExternalLink, BadgeCheck } from "lucide-react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

export default function Transparency() {
    const [impact, setImpact] = useState({ raised_usd: 0, donations_count: 0, goal_usd: 100000 });

    useEffect(() => {
        api.get("/impact/counter").then((r) => setImpact(r.data)).catch(() => {});
    }, []);

    return (
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
            <div className="max-w-2xl">
                <p className="text-xs uppercase tracking-widest text-primary mb-3">Transparency</p>
                <h1 className="font-display text-4xl md:text-5xl text-accent tracking-tight leading-tight">
                    Where your money goes.
                </h1>
                <p className="text-muted-foreground text-lg mt-4 leading-relaxed">
                    Real-Check is mission-driven, and we treat your trust the same way we treat facts —
                    with rigor. Here&apos;s exactly how every dollar is split.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-12">
                <Card className="card-surface p-6 rounded-2xl">
                    <Heart className="h-6 w-6 text-primary mb-3" />
                    <div className="font-display text-3xl text-accent">100%</div>
                    <p className="text-sm text-muted-foreground mt-1">
                        Direct donations to Hands of Hope. Zero platform cut.
                    </p>
                </Card>
                <Card className="card-surface p-6 rounded-2xl">
                    <PiggyBank className="h-6 w-6 text-primary mb-3" />
                    <div className="font-display text-3xl text-accent">50 / 50</div>
                    <p className="text-sm text-muted-foreground mt-1">
                        Pro subscriptions split evenly between Hands of Hope and the founders.
                    </p>
                </Card>
                <Card className="card-surface p-6 rounded-2xl">
                    <ShieldCheck className="h-6 w-6 text-primary mb-3" />
                    <div className="font-display text-3xl text-accent">Q&nbsp;Report</div>
                    <p className="text-sm text-muted-foreground mt-1">
                        Public quarterly transparency reports with itemized flows.
                    </p>
                </Card>
            </div>

            <section className="mt-16">
                <h2 className="font-display text-2xl text-accent mb-6">How every revenue stream flows</h2>
                <div className="space-y-3">
                    <RevenueRow name="Direct donations" hoh={100} founders={0} testid="row-donations" />
                    <RevenueRow name="Pro subscriptions ($4.99/mo, $39/yr)" hoh={50} founders={50} testid="row-pro" />
                    <RevenueRow name="Merch store (coming soon)" hoh={50} founders={50} testid="row-merch" />
                    <RevenueRow name="Ads & sponsorships (free tier)" hoh={50} founders={50} testid="row-ads" />
                </div>
            </section>

            <section className="mt-16 grid md:grid-cols-2 gap-6">
                <Card className="card-surface p-8 rounded-2xl">
                    <div className="flex items-center gap-2 mb-3">
                        <Users className="h-6 w-6 text-primary" />
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 inline-flex items-center gap-1" data-testid="hoh-verified-badge">
                            <BadgeCheck className="h-3 w-3" /> Verified charity
                        </span>
                    </div>
                    <h3 className="font-display text-xl text-accent mb-2">Who is Hands of Hope?</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        <strong className="text-accent">Hands of Hope</strong> was founded in 2025 by Chris,
                        a disabled veteran, to help people and animals who are struggling, hurting, or forgotten.
                        Real-Check.org was built by the same founder to bring truth, clarity, compassion, and
                        grounding to anyone who needs it — and to fund the Hands of Hope mission sustainably.
                        Every direct donation on this platform is passed through, in full, to Hands of Hope.
                    </p>
                </Card>
                <Card className="card-surface p-8 rounded-2xl">
                    <ShieldCheck className="h-6 w-6 text-primary mb-3" />
                    <h3 className="font-display text-xl text-accent mb-2">No dark patterns</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        We won&apos;t sell your data, run political ads, or hide the paywall. Free is genuinely free —
                        3 checks a day, no card required. Upgrade only if we&apos;ve earned it.
                    </p>
                </Card>
            </section>

            {/* One-site callout */}
            <Card className="card-surface p-8 rounded-2xl mt-6" data-testid="legacy-site-card">
                <div className="flex flex-wrap items-start gap-6 justify-between">
                    <div className="max-w-2xl">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-primary/20 text-accent border border-primary/40">
                                One promise
                            </span>
                        </div>
                        <h3 className="font-display text-xl text-accent mb-2">real-check.org</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            One site, one mission. Every dollar routed here goes to Hands of Hope,
                            the charity Chris founded in 2025 to help people and animals who need
                            somewhere safe to turn.
                        </p>
                    </div>
                    <a href="/donate" data-testid="legacy-site-link">
                        <Button variant="secondary" className="rounded-full">
                            Support the mission <ExternalLink className="h-4 w-4 ml-2" />
                        </Button>
                    </a>
                </div>
            </Card>

            <Card className="card-surface mt-12 p-8 rounded-2xl flex flex-wrap items-center gap-6 justify-between">
                <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Live impact</p>
                    <div className="font-display text-2xl text-accent" data-testid="transparency-total-raised">
                        ${impact.raised_usd.toLocaleString()} raised · {impact.donations_count} donations
                    </div>
                </div>
                <Link to="/donate">
                    <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="transparency-donate-cta">
                        <Heart className="h-4 w-4 mr-2" /> Donate now
                    </Button>
                </Link>
            </Card>
        </div>
    );
}

function RevenueRow({ name, hoh, founders, testid }) {
    return (
        <div className="card-surface rounded-xl p-4 flex items-center gap-4" data-testid={testid}>
            <div className="flex-1">
                <p className="text-sm text-accent font-medium">{name}</p>
                <div className="mt-2 h-2 rounded-full bg-white/5 overflow-hidden flex">
                    <div className="h-full bg-primary" style={{ width: `${hoh}%` }} />
                    <div className="h-full bg-emerald-400/70" style={{ width: `${founders}%` }} />
                </div>
            </div>
            <div className="text-right shrink-0">
                <div className="text-xs text-muted-foreground">Hands of Hope</div>
                <div className="font-display font-bold text-accent">{hoh}%</div>
            </div>
            <div className="text-right shrink-0">
                <div className="text-xs text-muted-foreground">Founders / Ops</div>
                <div className="font-display font-bold text-accent">{founders}%</div>
            </div>
        </div>
    );
}
